import * as React from 'react';
import SummariesCard from '../cards/SummariesCard';
import VirtualSummaryBody from './VirtualSummaryBody';
import PhysicalSummaryBody from './PhysicalSummaryBody';
import { tableViewModel } from '../models/views/tableViewModel';
import { virtualSummaryDto } from '../models/dto/virtualSummaryDto';
import { virtualResponseTransformer } from '../models/transformers/virtualResponseTransformer';
import GraphsSummaryBody from './GraphsSummaryBody';
import { vmResourceDistributionGraphDto } from '../models/dto/vmResourceDistributionGraphDto';
import { vmResourceDistributionGraphTransformer } from '../models/transformers/vmResourceDistributionGraphTransformer';
import { HorizontalGaugeBarSeries } from '@liveoptics/react-ui/dist/types/interfaces/Charts';
import { provisioningContrastGraphTransformer } from '../models/transformers/provisioningContrastGraphTransformer';
import { ProvisioningContrastPoint } from '../models/views/provisioningContrastGraphViewModel';
import { provisioningGraphsDto } from '../models/dto/provisioningGraphsDto';
import { OverProvisioningPoint } from '../models/views/overProvisioningGraphViewModel';
import { overProvisioningGraphTransformer } from '../models/transformers/overProvisioningGraphTransformer';

interface State {
    virtualData: {
        averageVMData: tableViewModel[];
        guestOSData: tableViewModel[];
        memoryData: tableViewModel[];
        vcpuData: tableViewModel[];
        capacityData: tableViewModel[];
    };
    physicalData: {
        physicalVMsData: tableViewModel[];
        physicalHostsData: tableViewModel[];
        physicalHostsStatsData: tableViewModel[];
    };
    graphsData: {
        vmResourceDistributionGraphData: HorizontalGaugeBarSeries[];
        provisioningContrastGraphData: ProvisioningContrastPoint[] | undefined;
        overProvisioningGraphData: OverProvisioningPoint[] | undefined;
    };
    noData: boolean;
}

class Summaries extends React.Component {
    state = {
        virtualData: {
            averageVMData: [],
            guestOSData: [],
            memoryData: [],
            vcpuData: [],
            capacityData: []
        },
        physicalData: {
            physicalVMsData: [],
            physicalHostsData: [],
            physicalHostsStatsData: []
        },
        graphsData: {
            vmResourceDistributionGraphData: [],
            provisioningContrastGraphData: undefined,
            overProvisioningGraphData: undefined
        },
        noData: false
    } as State;

    LoadDataFromAPI = async () => {
        var id = (window as any).Dpack.Project.Id;
        const summaryResponse = await fetch(`/api/opticalprime/${id}/virtualsummary`);

        if (summaryResponse.ok) {
            var vmResourceDistributionGraphData: HorizontalGaugeBarSeries[] = await this.FetchVmResourceDistributionGraphData(
                id
            );
            var [
                provisioningContrastGraphData,
                overProvisioningGraphData
            ] = await this.FetchProvisioningContrastGraphData(id);

            const json = (await summaryResponse.json()) as virtualSummaryDto;
            const data = virtualResponseTransformer.from(json);
            this.setState({
                virtualData: {
                    averageVMData: data.averageVMDataArray,
                    guestOSData: data.guestOSCountDataArray,
                    memoryData: data.memoryDataArray,
                    vcpuData: data.vcpuDataArray,
                    capacityData: data.capacityDataArray
                },
                physicalData: {
                    physicalVMsData: data.physicalVMsArray,
                    physicalHostsData: data.physicalHostsArray,
                    physicalHostsStatsData: data.physicalHostsStatsArray
                },
                graphsData: {
                    vmResourceDistributionGraphData: vmResourceDistributionGraphData,
                    provisioningContrastGraphData: provisioningContrastGraphData,
                    overProvisioningGraphData: overProvisioningGraphData
                }
            });
        } else {
            this.setState({ noData: true });
        }
    };

    async componentDidMount() {
        await this.LoadDataFromAPI();
    }

    private async FetchVmResourceDistributionGraphData(id: any) {
        const vmResourceGraphResponse = await fetch(`/api/opticalprime/${id}/vmresourcedistgraph`);
        var graphData: HorizontalGaugeBarSeries[] = [];
        if (vmResourceGraphResponse.ok) {
            const vmResourceDistJson = (await vmResourceGraphResponse.json()) as vmResourceDistributionGraphDto;
            graphData = vmResourceDistributionGraphTransformer.from(vmResourceDistJson);
        }

        return graphData;
    }

    private async FetchProvisioningContrastGraphData(id: any) {
        const provisioningContrastGraphResponse = await fetch(
            `/api/opticalprime/${id}/provisioninggraphs`
        );
        var provisioningContrastGraph: ProvisioningContrastPoint[] = [];
        var overProvisioningGraph: OverProvisioningPoint[] = [];
        if (provisioningContrastGraphResponse.ok) {
            const provisioningGraphJsonData = (await provisioningContrastGraphResponse.json()) as provisioningGraphsDto;

            provisioningContrastGraph = provisioningContrastGraphTransformer.from(
                provisioningGraphJsonData
            );
            overProvisioningGraph = overProvisioningGraphTransformer.from(provisioningGraphJsonData);
        }
        return [provisioningContrastGraph, overProvisioningGraph];
    }

    render() {
        RegisterRecalculationEvent(this.LoadDataFromAPI);

        return (
            <SummariesCard
                virtualSummaryTitle="Virtual Summary"
                physicalSummaryTitle="Physical Summary"
                graphSummaryTitle="Graphs"
                virtualSummary={
                    <VirtualSummaryBody noData={this.state.noData} data={this.state.virtualData} />
                }
                physicalSummary={
                    <PhysicalSummaryBody noData={this.state.noData} data={this.state.physicalData} />
                }
                graphSummary={<GraphsSummaryBody noData={this.state.noData} data={this.state.graphsData} />}
            />
        );
    }
}

const RegisterRecalculationEvent = (callback: Function) => {
    const recalculateFlag = { val: 0 };

    (window as any).recalculateVirtualTabSummaryProxy = new Proxy(recalculateFlag, {
        set: () => {
            callback();
            return true;
        }
    });
};

export default Summaries;
